import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import ThaiAddressInput from '../../sharedComponents/ThaiAddressInput';
import { smartParseAddress, buildCleanAddress } from '../../utils/thaiAddressData';
import useManageWeeklyShip from './useManageWeeklyShip';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../sharedComponents/ConfirmDialog';

const PLATFORM_WARNING =
  'กล่องของลูกค้า TamaChan platform — ลูกค้าเลือกวิธีรับและชำระเงินเองในแอป ' +
  'ถ้าดำเนินการแทน ระบบจะไม่ตัดเงิน (ค่าส่งกลับไทยยังค้างชำระ) ทำเฉพาะเมื่อจำเป็นเท่านั้น';

const WeeklyShipAction = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [boxData, setBoxData] = useState([]);
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [actionType, setActionType] = useState('');
  const [courier, setCourier] = useState('EMS');
  const [address, setAddress] = useState('');
  const [subdistrict, setSubdistrict] = useState('');
  const [district, setDistrict] = useState('');
  const [province, setProvince] = useState('');
  const [postcode, setPostcode] = useState('');
  const [remark, setRemark] = useState('');
  const [locationLink, setLocationLink] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pasteArea, setPasteArea] = useState('');
  const [parsedPreview, setParsedPreview] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(true);
  const [confirmDeAction, setConfirmDeAction] = useState(false);
  const gridRef = useRef();

  // TamaChan platform customers choose + pay on the site themselves, so their boxes
  // go last (stable sort keeps the box order within each part).
  const filteredBoxData = useMemo(() => {
    const rows = hideCompleted ? boxData.filter(row => !row.haveAction) : boxData;
    return [...rows].sort((a, b) => Number(!!a.isPlatform) - Number(!!b.isPlatform));
  }, [hideCompleted, boxData]);
  const completedCount = useMemo(
    () => boxData.filter(row => row.haveAction).length,
    [boxData]
  );
  const uniqueSelectedContacts = [...new Set(selectedBoxes.map(b => b.contact).filter(Boolean))];
  const hasMixedContacts = uniqueSelectedContacts.length > 1;

  // Fresh (not-yet-actioned) boxes in the current selection.
  const freshSelectedBoxes = selectedBoxes.filter(b => !b.haveAction);
  // Existing shipment groups this round for the selected customer — offered as
  // "add to existing address" targets so no address needs to be re-entered.
  const existingGroupsForSelected = useMemo(() => {
    if (hasMixedContacts || freshSelectedBoxes.length === 0) return [];
    const customerId = selectedBoxes[0]?.customerId;
    if (!customerId) return [];
    const seen = new Map();
    boxData.forEach(row => {
      if (row.customerId === customerId && row.haveAction && row.shipmentGroup?.id && !seen.has(row.shipmentGroup.id)) {
        seen.set(row.shipmentGroup.id, row.shipmentGroup);
      }
    });
    return [...seen.values()];
  }, [boxData, selectedBoxes, freshSelectedBoxes.length, hasMixedContacts]);

  // Selected boxes that are already actioned can be reverted to the unassigned
  // pool ("de-action"), so the admin can re-assign them to another address/method.
  const canDeAction = selectedBoxes.length > 0 && selectedBoxes.every(b => b.haveAction);

  // Choosing for a platform customer takes no payment (they pay when they choose on the
  // site), so it is only for when it is really needed — warn, and confirm on submit.
  const choosingForPlatform = freshSelectedBoxes.some(b => b.isPlatform);
  const confirmChoosingForPlatform = () => !choosingForPlatform || window.confirm(PLATFORM_WARNING);

  const { getWeeklyShipByShipDateId, processWeeklyShipGroupByAdmin, deActionWeeklyShip } = useManageWeeklyShip();

  const columnDefs = useMemo(() => [
    {
      headerName: 'Box No',
      field: 'boxNo',
      flex: 1,
      checkboxSelection: true,
      headerCheckboxSelection: true,
    },
    {
      headerName: 'Weight (kg)',
      field: 'weight',
      flex: 1,
      valueFormatter: (params) => params.value ? `${params.value} kg` : '-'
    },
    {
      headerName: 'Contact',
      field: 'contact',
      flex: 1,
    },
    {
      headerName: 'Smile Name',
      field: 'smileNameInput',
      flex: 1,
      cellRenderer: (params) => (
        <span>
          {params.value}
          {params.data.isPlatform && (
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
              TamaChan
            </span>
          )}
        </span>
      ),
    }
  ], []);

  const getRowStyle = useCallback((params) => {
    if (params.data.haveAction) {
      return { backgroundColor: '#d4edda' };
    }
    if (params.data.isPlatform) {
      return { backgroundColor: '#f5f3ff' };
    }
    return null;
  }, []);

  function onSelectionChanged() {
    const api = gridRef.current && gridRef.current.api ? gridRef.current.api : null;
    if (!api) {
      return;
    }
    const selectedNodes = api.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    console.log('✅ Selected Boxes:', selectedData);
    console.log('🔍 First Box Structure:', selectedData[0]);
    if (selectedData[0]) {
      console.log('👤 Customer ID Path:', selectedData[0].customerId);
      console.log('📦 ShipmentGroup:', selectedData[0].shipmentGroup);
      console.log('🚚 ShipmentGroup.courierTh:', selectedData[0].shipmentGroup?.courierTh);
    }
    setSelectedBoxes(selectedData);

    // Prefill form from previous submission (shipmentGroup) of the first selected box
    const first = selectedData[0];
    if (first && first.shipmentGroup) {
      const sg = first.shipmentGroup;
      const addr = sg.address || null;
      // receiveMethod expected values: 'delivery' | 'pickup' | 'lalamove'
      setActionType(sg.receiveMethod || '');
      // Prefill remark
      setRemark(sg.remark || '');
      if (sg.receiveMethod === 'delivery') {
        // Set courier from shipment group or default to EMS
        const validCouriers = ['EMS', 'FLASH', 'DHL'];
        setCourier(validCouriers.includes(sg?.courierTh) ? sg.courierTh : 'EMS');
        setAddress(addr?.address || '');
        setSubdistrict(addr?.subdistrict || '');
        setDistrict(addr?.district || '');
        setProvince(addr?.province || '');
        setPostcode(addr?.postcode || '');
      } else {
        // clear address fields for non-delivery methods
        setAddress('');
        setSubdistrict('');
        setDistrict('');
        setProvince('');
        setPostcode('');
      }
      if (sg.receiveMethod === 'lalamove') {
        setLocationLink(addr?.locationLink || '');
      } else {
        setLocationLink('');
      }
      // Prefill name and phone for both delivery and lalamove
      // Name and phone are stored in address object, not shipmentGroup
      if (sg.receiveMethod === 'delivery' || sg.receiveMethod === 'lalamove') {
        setName(addr?.name || sg.name || '');
        setPhone(addr?.phone || sg.phone || '');
      } else {
        setName('');
        setPhone('');
      }
    } else {
      // No previous data; do not force-clear action type, but clear address/location/remark to avoid stale values
      setRemark('');
      setAddress('');
      setSubdistrict('');
      setDistrict('');
      setProvince('');
      setPostcode('');
      setLocationLink('');
      setName('');
      setPhone('');
    }
  }

  const fetchBoxData = async (selectedShipDateId) => {
    if (selectedShipDateId && selectedShipDateId !== 'default') {
      setIsLoading(true);
      try {
        const response = await getWeeklyShipByShipDateId(selectedShipDateId);
        console.log('📦 Loaded Box Data:', response);
        if (response) {
          // Sort: haveAction false first, then by contact
          const sortedData = response.sort((a, b) => {
            if (a.haveAction !== b.haveAction) {
              return a.haveAction ? 1 : -1; // false first
            }
            return a.contact.localeCompare(b.contact);
          });
          console.log('📦 Sorted Box Data:', sortedData);
          setBoxData(sortedData);
        } else {
          toast.error('ไม่สามารถโหลดข้อมูลได้');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
      } finally {
        setIsLoading(false);
      }
    } else {
      setBoxData([]);
    }
  };

  const handleShipDateChange = async (e) => {
    const selectedShipDateId = e.target.value;
    setShipDateId(selectedShipDateId);
    await fetchBoxData(selectedShipDateId);
  };

  const handleActionTypeChange = (e) => {
    setActionType(e.target.value);
    // Clear form when action type changes
    setCourier('EMS');
    setAddress('');
    setSubdistrict('');
    setDistrict('');
    setProvince('');
    setPostcode('');
    setRemark('');
    setLocationLink('');
    setName('');
    setPhone('');
    setPasteArea('');
    setParsedPreview(null);
    setSelectedLocation(null);
  };

  // Handle paste text change using smartParseAddress
  const handlePasteAreaChange = (e) => {
    const text = e.target.value;
    setPasteArea(text);
    setSelectedLocation(null);
    
    if (text.trim()) {
      const parsed = smartParseAddress(text);
      setParsedPreview(parsed);
      // Auto-select if exact match found
      if (parsed.exactMatch) {
        setSelectedLocation(parsed.exactMatch);
        applyParsedData(parsed, parsed.exactMatch);
      }
    } else {
      setParsedPreview(null);
    }
  };

  // Handle location selection from candidates
  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    if (parsedPreview) {
      applyParsedData(parsedPreview, location);
    }
  };

  // Apply parsed address data to form fields
  const applyParsedData = (parsed, locationData) => {
    if (!parsed) return;

    const location = locationData || {};
    
    // Build clean address based on selected location
    let cleanAddress = buildCleanAddress(
      parsed.rawText || pasteArea,
      { name: parsed.name, phone: parsed.phone, postcode: parsed.postcode },
      location
    );
    
    // Additional cleanup: Remove location abbreviations and terms
    // Remove: อ. ต. อำเภอ จังหวัด กทม กรุงเทพ
    if (location.subdistrict) {
      cleanAddress = cleanAddress.replace(new RegExp(`(ตำบล|ต\\.)\\s*${location.subdistrict}`, 'gi'), ' ');
    }
    if (location.district) {
      cleanAddress = cleanAddress.replace(new RegExp(`(อำเภอ|อ\\.)\\s*${location.district}`, 'gi'), ' ');
    }
    if (location.province) {
      cleanAddress = cleanAddress.replace(new RegExp(`(จังหวัด|จ\\.)\\s*${location.province}`, 'gi'), ' ');
      // Also remove common abbreviations for Bangkok
      if (location.province === 'กรุงเทพมหานคร' || location.province.includes('กรุงเทพ')) {
        cleanAddress = cleanAddress.replace(/\b(กรุงเทพ|กทม|BKK)\b/gi, ' ');
      }
    }
    
    // Clean up multiple spaces and trim
    cleanAddress = cleanAddress.replace(/\s+/g, ' ').trim();
    
    // Apply name
    if (parsed.name) {
      setName(parsed.name);
    }
    
    // Apply phone
    if (parsed.phone) {
      setPhone(parsed.phone);
    }
    
    // Apply clean address (only for delivery)
    if (actionType === 'delivery') {
      setAddress(cleanAddress);
    }
    
    // Apply location data from selection
    if (location.subdistrict) setSubdistrict(location.subdistrict);
    if (location.district) setDistrict(location.district);
    if (location.province) setProvince(location.province);
    if (location.postcode || parsed.postcode) {
      setPostcode(location.postcode || parsed.postcode || '');
    }
  };

  // Reset every form field + clear the grid selection back to the initial state.
  const resetForm = () => {
    // Clear selection state directly: after a submit the actioned rows are hidden
    // from the grid, so deselectAll() fires no selectionChanged event and would
    // otherwise leave selectedBoxes stale (right panel keeps showing the boxes).
    setSelectedBoxes([]);
    setActionType('');
    setCourier('EMS');
    setAddress('');
    setSubdistrict('');
    setDistrict('');
    setProvince('');
    setPostcode('');
    setRemark('');
    setLocationLink('');
    setName('');
    setPhone('');
    setPasteArea('');
    setParsedPreview(null);
    setSelectedLocation(null);
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.deselectAll();
    }
  };

  const handleSubmit = async () => {
    if (selectedBoxes.length === 0) {
      toast.warning('กรุณาเลือกกล่องที่ต้องการดำเนินการ');
      return;
    }
    if (!confirmChoosingForPlatform()) return;

    // Check if all selected boxes have the same contact
    const contacts = [...new Set(selectedBoxes.map(box => box.contact))];
    if (contacts.length > 1) {
      toast.error(`ไม่สามารถเลือกกล่องจากลูกค้าต่างกันได้ กรุณาเลือกกล่องจากลูกค้าเดียวกันเท่านั้น\nลูกค้าที่เลือก: ${contacts.join(', ')}`);
      return;
    }

    // Check if contact exists and is not empty
    const hasEmptyContact = selectedBoxes.some(box => !box.contact || box.contact.trim() === '');
    if (hasEmptyContact) {
      toast.error('ไม่สามารถดำเนินการได้ เนื่องจากมีกล่องที่ไม่มีข้อมูลติดต่อ');
      return;
    }

    if (!actionType) {
      toast.warning('กรุณาเลือกประเภทการดำเนินการ');
      return;
    }

    if (actionType === 'delivery') {
      if (!courier || !subdistrict || !district || !province || !postcode) {
        toast.warning('กรุณากรอกข้อมูลขนส่งและที่อยู่ให้ครบถ้วน');
        return;
      }
      if (!name || !phone) {
        toast.warning('กรุณากรอกชื่อและเบอร์โทรสำหรับการส่งพัสดุ');
        return;
      }
    }

    if (actionType === 'lalamove') {
      if (!locationLink) {
        toast.warning('กรุณากรอกลิงก์ตำแหน่งสำหรับ Lalamove');
        return;
      }
      if (!name || !phone) {
        toast.warning('กรุณากรอกชื่อและเบอร์โทรสำหรับ Lalamove');
        return;
      }
    }

    // Prepare data for submission
    const submissionData = {
      weeklyShipIds: selectedBoxes.map(box => box.weeklyShipId),
      receiveMethod: actionType,
      customerId: selectedBoxes[0].customerId, // All boxes have same contact, so use first one
      address: actionType === 'delivery' ? address : null,
      province: actionType === 'delivery' ? province : null,
      postcode: actionType === 'delivery' ? postcode : null,
      district: actionType === 'delivery' ? district : null,
      subdistrict: actionType === 'delivery' ? subdistrict : null,
      locationLink: actionType === 'lalamove' ? locationLink : null,
      name: (actionType === 'delivery' || actionType === 'lalamove') ? name : null,
      phone: (actionType === 'delivery' || actionType === 'lalamove') ? phone : null,
      remark: remark || null,
      courierTh: actionType === 'delivery' ? courier : null
    };

    console.log('Submission data:', submissionData);

    setIsSubmitting(true);
    // Call the API
    const result = await processWeeklyShipGroupByAdmin(submissionData);
    setIsSubmitting(false);
    if (result) {
      toast.success(`ดำเนินการสำเร็จ: ${actionType} สำหรับ ${selectedBoxes.length} กล่อง`);
      // Refresh the data
      await fetchBoxData(shipDateId);
    } else {
      toast.error('เกิดข้อผิดพลาดในการดำเนินการ');
      setIsSubmitting(false);
      return;
    }
    
    // Clear form
    resetForm();
  };

  const receiveMethodLabel = (m) =>
    m === 'delivery' ? 'ส่งพัสดุ' : m === 'pickup' ? 'ไปรับเอง' : m === 'lalamove' ? 'Lalamove' : m || '-';

  const groupAddressSummary = (group) => {
    if (group.receiveMethod === 'pickup') return 'ไปรับเอง';
    if (group.receiveMethod === 'lalamove') return group.address?.locationLink || 'Lalamove';
    const a = group.address || {};
    return [a.address, a.subdistrict, a.district, a.province, a.postcode].filter(Boolean).join(' ') || '-';
  };

  // Attach the selected fresh boxes to an existing shipment group, reusing its
  // address/method — no re-entry needed.
  const handleAddToExisting = async (group) => {
    if (freshSelectedBoxes.length === 0) return;
    if (!confirmChoosingForPlatform()) return;
    setIsSubmitting(true);
    const result = await processWeeklyShipGroupByAdmin({
      weeklyShipIds: freshSelectedBoxes.map(box => box.weeklyShipId),
      customerId: selectedBoxes[0].customerId,
      receiveMethod: group.receiveMethod,
      targetShipmentGroupId: group.id
    });
    setIsSubmitting(false);
    if (result) {
      toast.success(`เพิ่ม ${freshSelectedBoxes.length} กล่องเข้ากลุ่มเดิม (${group.shipmentNo || group.id}) สำเร็จ`);
      await fetchBoxData(shipDateId);
      resetForm();
    } else {
      toast.error('เกิดข้อผิดพลาดในการเพิ่มเข้ากลุ่มเดิม');
    }
  };

  // Revert the selected actioned boxes back to the unassigned pool so they can be
  // re-selected and assigned to another address/method. Other boxes in the same
  // group are untouched.
  // De-action of a TamaChan platform customer's choice: only on the customer's request.
  // The backend alerts the Manager group and tells the customer to choose again; nothing
  // is refunded. Ask first (confirm locked 3 s for platform), and warn about boxes that
  // already have Thai tracking.
  const deActionPlatform = canDeAction && selectedBoxes.some(b => b.isPlatform);
  const deActionShipped = canDeAction && selectedBoxes.some(b => b.hasTrackingTh);
  const deActionWarning = [
    deActionPlatform && 'กล่องของลูกค้า TamaChan platform — ยกเลิกได้เฉพาะเมื่อลูกค้าขอเท่านั้น\n' +
      '• ไม่มีการคืนเงิน ลูกค้าต้องเลือกวิธีรับใหม่ในแอป (ไม่ถูกตัดเงินซ้ำ)\n' +
      '• ระบบจะแจ้งกลุ่ม Manager และส่งข้อความแจ้งลูกค้าทันที',
    deActionShipped && '⚠️ บางกล่องมีเลขพัสดุในไทยแล้ว (ส่งออก / เก็บค่าส่งไปแล้ว)',
  ].filter(Boolean).join('\n\n');

  const handleDeAction = () => {
    if (!canDeAction) return;
    if (deActionWarning) {
      setConfirmDeAction(true);
      return;
    }
    runDeAction();
  };

  const runDeAction = async () => {
    setConfirmDeAction(false);
    setIsSubmitting(true);
    const result = await deActionWeeklyShip(selectedBoxes.map(box => box.weeklyShipId));
    setIsSubmitting(false);
    if (result) {
      toast.success(`ยกเลิก / แก้ไข ${selectedBoxes.length} กล่อง — พร้อมเลือกใหม่แล้ว`);
      await fetchBoxData(shipDateId);
      resetForm();
    } else {
      toast.error('เกิดข้อผิดพลาดในการคืนค่า');
    }
  };

  return (
    <div className="h-screen grid grid-cols-2 gap-6 p-6">
      {/* Left Side - AgGrid Table */}
      <div className="flex flex-col">
        <div className="mb-3">
          <ShipDateDropdown 
            handleDataChange={handleShipDateChange}
            includeDefault={true}
            airOnly={true}
          />
        </div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            แสดง {filteredBoxData.length} รายการ
            {completedCount > 0 && hideCompleted && ` (ซ่อน ${completedCount} รายการที่ดำเนินการแล้ว)`}
          </span>
          {completedCount > 0 && (
            <button
              onClick={() => setHideCompleted(prev => !prev)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                hideCompleted
                  ? 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  : 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200'
              }`}
            >
              {hideCompleted ? `แสดงทั้งหมด (รวม ${completedCount} เสร็จแล้ว)` : 'ซ่อนรายการที่เสร็จแล้ว'}
            </button>
          )}
        </div>
        
        <div className="flex-1">
          <div className="h-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-lg">กำลังโหลดข้อมูล...</div>
              </div>
            ) : (
              <AgGridReact
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={filteredBoxData}
                defaultColDef={{ resizable: true }}
                rowSelection="multiple"
                rowMultiSelectWithClick={true}
                onSelectionChanged={onSelectionChanged}
                getRowStyle={getRowStyle}
                enableCellTextSelection={true}
                suppressRowVirtualisation={true}
                className="ag-theme-alpine"
                style={{ height: '100%', width: '100%' }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex flex-col">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold mb-6"></h2>
          
          {/* Selected Boxes Info */}
          {selectedBoxes.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold mb-2">กล่องที่เลือก ({selectedBoxes.length} กล่อง):</h3>
              <div className="space-y-1">
                {selectedBoxes.map((box, index) => (
                  <div key={index} className="text-sm">
                    {box.boxNo} - {box.contact} ({box.weight} kg)
                  </div>
                ))}
              </div>
            </div>
          )}

          {choosingForPlatform && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-300 rounded-lg text-sm text-purple-800">
              <div className="font-semibold mb-1">⚠️ ลูกค้า TamaChan platform</div>
              <div>{PLATFORM_WARNING}</div>
            </div>
          )}

          {/* Add-to-existing shipment: reuse an address already set this round */}
          {existingGroupsForSelected.length > 0 && (
            <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg">
              <h3 className="font-semibold mb-1 text-green-800">
                เพิ่มเข้าที่อยู่เดิมของลูกค้า (ไม่ต้องกรอกที่อยู่ใหม่)
              </h3>
              <p className="text-xs text-green-700 mb-3">
                ลูกค้านี้มีการดำเนินการในรอบนี้แล้ว — เลือกกลุ่มเดิมเพื่อเพิ่ม {freshSelectedBoxes.length} กล่องที่เลือกเข้าไป
              </p>
              <div className="space-y-2">
                {existingGroupsForSelected.map((group) => (
                  <div key={group.id} className="flex items-center justify-between gap-3 p-3 bg-white border border-green-200 rounded-lg">
                    <div className="text-sm min-w-0">
                      <div className="font-medium">
                        {receiveMethodLabel(group.receiveMethod)}
                        {group.receiveMethod === 'delivery' && group.courierTh ? ` · ${group.courierTh}` : ''}
                        {group.shipmentNo ? ` · ${group.shipmentNo}` : ''}
                      </div>
                      <div className="text-xs text-gray-600 break-words">{groupAddressSummary(group)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddToExisting(group)}
                      disabled={isSubmitting || freshSelectedBoxes.length === 0}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
                        isSubmitting || freshSelectedBoxes.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      เพิ่มเข้ากลุ่มนี้
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mixed-contact warning */}
          {hasMixedContacts && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-300 rounded-lg">
              <p className="text-orange-700 text-sm font-semibold">
                ⚠️ คำเตือน: รายการที่เลือกมาจากลูกค้าต่างกัน
              </p>
              <p className="text-orange-600 text-xs mt-1">
                {uniqueSelectedContacts.join(' · ')}
              </p>
              <p className="text-orange-500 text-xs mt-0.5">
                กรุณาตรวจสอบว่าเลือกถูกต้อง
              </p>
            </div>
          )}

          {/* Action Type Selection */}
          <div className="mb-6">
            <label className="block text-lg font-semibold mb-2">ประเภทการดำเนินการ *</label>
            <select
              value={actionType}
              onChange={handleActionTypeChange}
              className="w-full p-3 border border-gray-300 rounded-lg text-lg"
            >
              <option value="">-- เลือกประเภทการดำเนินการ --</option>
              <option value="delivery">ส่งพัสดุ</option>
              <option value="pickup">ไปรับเอง</option>
              <option value="lalamove">Lalamove</option>
            </select>
          </div>

          {/* Courier Input (only for ส่งพัสดุ) */}
          {actionType === 'delivery' && (
            <div className="mb-6">
              <label className="block text-lg font-semibold mb-2">ขนส่ง *</label>
              <select
                value={courier}
                onChange={(e) => setCourier(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg text-lg"
              >
                <option value="EMS">EMS</option>
                <option value="FLASH">FLASH</option>
                <option value="DHL">DHL</option>
              </select>
            </div>
          )}

          {/* Paste Area for Auto-Extraction (for delivery and lalamove) */}
          {(actionType === 'delivery' || actionType === 'lalamove') && (
            <div className="mb-6">
              <label className="block text-lg font-semibold mb-2">
                วางข้อมูลที่นี่ (จะดึงข้อมูลอัตโนมัติ: ชื่อ, เบอร์, รหัสไปรษณีย์, อำเภอ, ตำบล)
              </label>
              <textarea
                value={pasteArea}
                onChange={handlePasteAreaChange}
                placeholder="วางข้อมูลที่อยู่และข้อมูลติดต่อที่นี่..."
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg text-lg resize-none"
              />

              {/* Extracted basic info (name, phone, postcode) */}
              {parsedPreview && (
                <div className="bg-blue-50 p-3 rounded-lg mt-2">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">ข้อมูลที่ดึงได้:</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">ชื่อ:</span>
                      <span className="ml-2 font-medium text-blue-700">{parsedPreview.name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">เบอร์โทร:</span>
                      <span className="ml-2 font-medium text-blue-700">{parsedPreview.phone || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">รหัสไปรษณีย์:</span>
                      <span className="ml-2 font-medium text-blue-700">{parsedPreview.postcode || '-'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Location selection - REQUIRED */}
              {parsedPreview?.candidates?.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-gray-700 text-sm">
                      {parsedPreview.exactMatch 
                        ? '✓ พบที่อยู่ที่ตรงกัน'
                        : '⚠️ กรุณาเลือกที่อยู่ที่ถูกต้อง'
                      }
                    </h4>
                    {!parsedPreview.exactMatch && parsedPreview.needsSelection && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                        * ต้องเลือก
                      </span>
                    )}
                  </div>

                  {/* Show as buttons/cards */}
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                    {parsedPreview.candidates.slice(0, 6).map((loc, index) => {
                      const isSelected = selectedLocation === loc;
                      const isMatch = loc.matchedParts?.length >= 3;
                      
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelectLocation(loc)}
                          className={`px-3 py-2 rounded-lg border-2 text-left transition-all text-sm ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : isMatch
                              ? 'border-green-300 bg-green-50 hover:border-green-400'
                              : 'border-gray-200 bg-white hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="font-medium">{loc.subdistrict}</div>
                              <div className="text-xs text-gray-600">
                                {loc.district}, {loc.province}
                              </div>
                            </div>
                            {isSelected && (
                              <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Thai Address Input with autocomplete (only for delivery) */}
          {actionType === 'delivery' && (
            <div className="mb-6">
              <ThaiAddressInput
                address={address}
                subdistrict={subdistrict}
                district={district}
                province={province}
                postcode={postcode}
                name={name}
                phone={phone}
                onAddressChange={setAddress}
                onSubdistrictChange={setSubdistrict}
                onDistrictChange={setDistrict}
                onProvinceChange={setProvince}
                onPostcodeChange={setPostcode}
                onNameChange={setName}
                onPhoneChange={setPhone}
                showName={true}
                showPhone={true}
                showAddress={true}
                showValidation={true}
                hidePasteButton={true}
                inputClassName="w-full p-2 border border-gray-300 rounded-lg text-base"
                labelClassName="block text-sm font-semibold mb-1"
              />
            </div>
          )}

          {/* Location Link Input (only for Lalamove) */}
          {actionType === 'lalamove' && (
            <div className="mb-6">
              <label className="block text-lg font-semibold mb-2">ลิงก์ตำแหน่ง *</label>
              <input
                type="url"
                value={locationLink}
                onChange={(e) => setLocationLink(e.target.value)}
                placeholder="กรอกลิงก์ตำแหน่งจาก Lalamove"
                className="w-full p-3 border border-gray-300 rounded-lg text-lg"
              />
            </div>
          )}

          {/* Name and Phone Inputs (only for lalamove) */}
          {actionType === 'lalamove' && (
            <div className="mb-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1">ชื่อผู้รับ *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ชื่อผู้รับ"
                  className="w-full p-2 border border-gray-300 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">เบอร์โทร *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="เบอร์โทร"
                  className="w-full p-2 border border-gray-300 rounded-lg text-base"
                />
              </div>
            </div>
          )}

          {/* Remark Field (for all action types) */}
          <div className="mb-6">
            <label className="block text-lg font-semibold mb-2">หมายเหตุ</label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="กรอกหมายเหตุเพิ่มเติม (ถ้ามี)"
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg text-lg resize-none"
            />
          </div>

          {/* De-action hint — selected boxes are already actioned */}
          {canDeAction && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm">
              <p className="text-amber-700 text-xs">
                รายการที่เลือกดำเนินการไปแล้ว — กด “ยกเลิก / แก้ไข” เพื่อนำกลับไปเลือกที่อยู่/วิธีจัดส่งใหม่ (กล่องอื่นในกลุ่มเดิมไม่ถูกแตะต้อง)
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={selectedBoxes.length === 0 || !actionType || isSubmitting}
            className={`w-full py-3 px-6 rounded-lg text-lg font-semibold ${
              selectedBoxes.length === 0 || !actionType || isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'กำลังดำเนินการ...' : 'ดำเนินการ'}
          </button>

          <ConfirmDialog
            open={confirmDeAction}
            title="ยืนยันยกเลิกวิธีรับสินค้า"
            message={deActionWarning}
            confirmText="ยืนยันยกเลิก"
            confirmDelaySeconds={deActionPlatform ? 3 : 0}
            onConfirm={runDeAction}
            onClose={() => setConfirmDeAction(false)}
          />

          {/* De-action Button — only when every selected box is already actioned */}
          {canDeAction && (
            <button
              onClick={handleDeAction}
              disabled={isSubmitting}
              className={`w-full mt-3 py-3 px-6 rounded-lg text-lg font-semibold ${
                isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {isSubmitting ? 'กำลังดำเนินการ...' : `ยกเลิก / แก้ไข (รอเลือกใหม่) · ${selectedBoxes.length} กล่อง`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyShipAction;
